import { FC, ReactElement, useMemo, type CSSProperties } from "react";
import styled, { createGlobalStyle, css, useTheme } from 'styled-components';
import { LayoutConst } from 'core/modules';
import { Colors, DropdownListItem, FlexBox, Dropdown, useIsMobile, useAdvancedLockBodyScroll, ExternalLink, useBreakpoint } from "@brg/ui";
import ArrowTopIconWithText from "shared/icons/ArrowTopIconWithText";
import { ApprovedIcon } from "shared/icons/ApprovedIcon";
import { useTranslation } from "react-i18next";
import { MoreButton } from "shared/components/more-button";
import { AppTester } from "supporting";
import { ApplicationTheme } from "shared/themes";

const REF_NAV_LOGIN_DROPDOWN_CONTENT_BG = {
  grey: ApplicationTheme.grey.navigationBar.login.dropdownContentBg,
  additionalGrey: ApplicationTheme.additionalGreyTheme.navigationBar.login.dropdownContentBg,
  black: ApplicationTheme.black.navigationBar.login.dropdownContentBg,
} as const;

const ExternalsDarkDropdownScrollbar = createGlobalStyle`
  .brg-externals-dropdown--dark
    .dropdown-show.dropdown-element-popup
    .dropdown-element-content::-webkit-scrollbar,
  .brg-externals-dropdown--dark
    .dropdown-show.dropdown-element-popup
    .dropdown-element-content::-webkit-scrollbar-track {
    background: var(
      --brg-externals-dropdown-scrollbar-bg,
      ${({ theme }) => theme.navigationBar.login.dropdownContentBg}
    ) !important;
  }
`;

const DropdownItem = styled(DropdownListItem)<{ disabled?: boolean; $competitionRow?: boolean }>`
  ${({ disabled, theme }) => disabled && css``}

  &.dafes-countries {
    padding: 0;
  }
  ${({ $competitionRow, theme }) =>
    $competitionRow &&
    css`
      && {
        background: ${(theme.competition?.langComponent?.notActive?.bg ??
          theme.navigationBar.login.dropDownBackground)} !important;
      }
      &&:hover {
        background: ${theme.navigationBar.login.dropDownHoverBackground} !important;
      }
    `}
`

const DropdownWrapper = styled(FlexBox) <{ isTargetEmpty?: boolean, isNewCha?: boolean }>`

  ${({ isTargetEmpty }) => isTargetEmpty
    ? css`
    margin-left: 0px;
  ` : css`
    margin-left: 24px;
  `};

  @media (max-width: ${LayoutConst.screen.l_16}px) {
    ${({ isTargetEmpty }) => isTargetEmpty
    ? css`
    margin-left: 0px;
  ` : css`
    margin-left: 14px;
  `};
  }

  @media (max-width: 1440px) {
    ${({ isTargetEmpty }) => !isTargetEmpty && css`
      margin-left: 16px;
    `};
  }

  ${({ isNewCha }) => isNewCha && css`
    svg.moreIcon path { fill: #FFFFFF !important; }
  `};
`;

const DropdownBodyWrapper = styled.div`

  .cs-container {
    > div {
      justify-content: flex-start; 
    }
  }
  
  overflow-y: scroll;
  height: 100%;

  ::-webkit-scrollbar {
    width: 3px;
    background: ${(p) => p.theme.mobileSearch.scroll.background};
  }
  ::-webkit-scrollbar-thumb {
    background: ${(p) => p.theme.mobileSearch.scroll.body};
  }
  ::-webkit-scrollbar-track {
    background: ${(p) => p.theme.mobileSearch.scroll.background};
  }
`;

export const DropdownText = styled(ExternalLink) <{ isActive?: boolean, isMedia?: boolean, disabled?: boolean }>`
  width: 100%;
  font-size: ${({ isMedia }) => isMedia ? '20px' : '17px'};
  line-height: ${({ isMedia }) => isMedia ? '25px' : '22px'};
  letter-spacing: -0.02em;
  font-weight: 600 !important;
  color: ${p => {
    if (p?.isActive) return p.theme.mainDropdownStyles.colorActive;
    if (p?.disabled) return p.theme.mainDropdownStyles.disabled;
    return p.theme.mainDropdownStyles.color;
  }} !important;

  ${({ disabled }) => disabled && css`
    cursor: not-allowed;
  `};
  
  @media(max-width: ${LayoutConst.screen.l_16}px) {
    font-size: 15px;
    line-height: 20px;
  }

  @media (max-width: ${LayoutConst.screen.md}px) {
    font-size: 13px;
  }
`;

interface IExternalLinks {
  title: string | (() => string) | JSX.Element;
  value?: string | (() => string);
  ctx: string;
  active: boolean;
  external: boolean;
  isModal?: boolean;
  action?: (e?: any, unlockBody?: (withoutCheck?: boolean) => void) => void;
  isTeam?: boolean;
  name: string;
  disabled?: boolean;
  target?: string;
  className?: string;
}

interface IProps {
  target?: { [key: string]: string },
  list: {
    [key: string]: string,
  } | (string | { active: boolean, content: string, linkTo: string })[];
  initialTab?: string;
  hoverOpen?: boolean;
  withExternal?: boolean;
  isDark?: boolean;
}

export const DropdownWithExternalsLinks: FC<{
  target?: ReactElement;
  list: IExternalLinks[];
  initialTab?: string;
  hoverOpen?: boolean;
  maxHeight?: number;
  maxWidth?: number;
  customTarget?: boolean;
  isDarkTheme?: boolean;
  useCompetitionPanelColors?: boolean;
  noMargin?: boolean;
  isCustomTitle?: boolean;
  disableLink?: boolean;
}> = ({
  target,
  list,
  hoverOpen,
  maxHeight,
  isDarkTheme = false,
  maxWidth,
  customTarget,
  noMargin,
  isCustomTitle,
  disableLink = false,
  useCompetitionPanelColors = false,
}) => {
    const { t } = useTranslation();
    const theme = useTheme();
    const isDeziiignMain = AppTester.isDeziiignMain();
    const isHseDesign = AppTester.isHSEDesign();
    const isKiiids = AppTester.isKiiidsArt();
    const isMobile = useIsMobile();
    const isTablet = useBreakpoint(1199)
    const isAcademy = AppTester.isAcademy();
    const isFtbl = AppTester.isFtbl();
    const isPromtWeekend = AppTester.isPromtWeekend();
    const isNewCha = AppTester.isNewCha();

    const targetElem = target || '';
    const [lockBody, unlockBody] = useAdvancedLockBodyScroll();

    const isTargetEmpty = useMemo(() => (targetElem === '' || noMargin), [targetElem, noMargin])

    const dropdownTarget = useMemo(() => {
      return customTarget ? target :
        isDeziiignMain ? (
          <MoreButton />
        ) : (
          <ArrowTopIconWithText
            fill={'#909090'}
            text={''}
            noIconPadding
          />
        )
    }, [customTarget, isDeziiignMain, target])

    if (!list) {
      return null;
    }

    const customTheming = theme.navigationBar.login;
    const darkDropdownPanelBg =
      useCompetitionPanelColors && theme.competition?.langComponent?.notActive?.bg
        ? theme.competition.langComponent.notActive.bg
        : customTheming.dropdownContentBg;

    const currentNavPanelBg = theme.navigationBar.login.dropdownContentBg;
    const isGreyNavLoginPanel =
      currentNavPanelBg === REF_NAV_LOGIN_DROPDOWN_CONTENT_BG.grey ||
      currentNavPanelBg === REF_NAV_LOGIN_DROPDOWN_CONTENT_BG.additionalGrey;
    const isBlackNavLoginPanel = currentNavPanelBg === REF_NAV_LOGIN_DROPDOWN_CONTENT_BG.black;
    const brgDropdownUseDarkFlag =
      isDarkTheme && (isBlackNavLoginPanel || !isGreyNavLoginPanel);

    const items = useMemo(() => {
      return  Array.isArray(list) && list?.map((item, idx) => {
        const {
          title: initialTitle,
          isTeam,
          action,
          external,
          value,
          active,
          disabled,
          target: linkTargetInitial,
          className,
        } = item;

        const title = (() => {
          const resolvedTitle = typeof initialTitle === 'function' ? initialTitle() : initialTitle;
          if (typeof resolvedTitle === 'object') {
            return resolvedTitle;
          }
          if (isCustomTitle || isTeam) {
            return resolvedTitle?.toLowerCase();
          }

          return resolvedTitle;
        })();

        const linkTarget = linkTargetInitial || (external ? '_blank' : '_self');
        const tooltipTitle = typeof title === 'string' ? title : undefined;
        const isDafesCountries = className === 'dafes-countries'

        return (
          <DropdownItem
            $competitionRow={useCompetitionPanelColors}
            style={{ cursor: 'pointer' }}
            key={idx}
            justify='space-between'
            isActive={false}
            disabled={disabled}
            disabledHover
            theming={isAcademy ? customTheming : ''}
            className={className as never}
          >
            <DropdownText
              to={disableLink ? undefined : typeof value === 'function' ? value() : value}
              target={linkTarget}
              title={tooltipTitle}
              disabled={disabled}
              onClick={(ev: any) => {
                if (disabled) {
                  ev.preventDefault();
                }
                if (action) {
                  disabled ? null : action?.(ev, unlockBody);
                }
              }}
            >
              {title}
            </DropdownText>
            {!isDafesCountries && <ApprovedIcon fill={Colors.galBlack100} display={active}/>}
          </DropdownItem>
        )
      })
    }, [list, disableLink, useCompetitionPanelColors, isAcademy, isCustomTitle, theme])

    return (
      <DropdownWrapper
        justify="center"
        align="center"
        isTargetEmpty={isTargetEmpty}
        isNewCha={isNewCha}
        className={isDarkTheme ? 'brg-externals-dropdown--dark' : undefined}
        style={
          isDarkTheme
            ? ({
                ['--brg-externals-dropdown-scrollbar-bg' as string]: darkDropdownPanelBg,
              } as CSSProperties)
            : undefined
        }
      >
        {isDarkTheme ? <ExternalsDarkDropdownScrollbar /> : null}
        <Dropdown
          offsetY={12}
          offsetX={isHseDesign ? (isMobile ? -80 : -180) : -100}
          dark={brgDropdownUseDarkFlag}
          {...(isDarkTheme
            ? { dropdownPopupBg: darkDropdownPanelBg, dropdownContentBg: darkDropdownPanelBg }
            : {})}
          offsetArrowX={26}
          offsetArrowY={0}
          hideArrow
          hoverOpen={!isKiiids && !isTablet}
          maxHeight={maxHeight}
          maxWidth={maxWidth}
          target={dropdownTarget}
        >
          <DropdownBodyWrapper
            onMouseEnter={() => lockBody()}
            onClick={() => {
                if (isFtbl || isPromtWeekend) return;
                lockBody();
            }}
            onMouseLeave={() => unlockBody(false)}
          >
            {items}
          </DropdownBodyWrapper>
        </Dropdown>
      </DropdownWrapper>
    )
  }
import { FC, ReactElement, useMemo, type CSSProperties } from "react";
import styled, { createGlobalStyle, css, useTheme } from 'styled-components';
import { LayoutConst } from 'core/modules';
import { Colors, DropdownListItem, FlexBox, Dropdown, useIsMobile, useAdvancedLockBodyScroll, ExternalLink, useBreakpoint } from "@brg/ui";
import ArrowTopIconWithText from "shared/icons/ArrowTopIconWithText";
import { ApprovedIcon } from "shared/icons/ApprovedIcon";
import { useTranslation } from "react-i18next";
import { MoreButton } from "shared/components/more-button";
import { AppTester } from "supporting";
import { ApplicationTheme } from "shared/themes";

const REF_NAV_LOGIN_DROPDOWN_CONTENT_BG = {
  grey: ApplicationTheme.grey.navigationBar.login.dropdownContentBg,
  additionalGrey: ApplicationTheme.additionalGreyTheme.navigationBar.login.dropdownContentBg,
  black: ApplicationTheme.black.navigationBar.login.dropdownContentBg,
} as const;

const ExternalsDarkDropdownScrollbar = createGlobalStyle`
  .brg-externals-dropdown--dark
    .dropdown-show.dropdown-element-popup
    .dropdown-element-content::-webkit-scrollbar,
  .brg-externals-dropdown--dark
    .dropdown-show.dropdown-element-popup
    .dropdown-element-content::-webkit-scrollbar-track {
    background: var(
      --brg-externals-dropdown-scrollbar-bg,
      ${({ theme }) => theme.navigationBar.login.dropdownContentBg}
    ) !important;
  }
`;

const DropdownItem = styled(DropdownListItem)<{ disabled?: boolean; $competitionRow?: boolean }>`
  ${({ disabled, theme }) => disabled && css``}

  &.dafes-countries {
    padding: 0;
  }
  ${({ $competitionRow, theme }) =>
    $competitionRow &&
    css`
      && {
        background: ${(theme.competition?.langComponent?.notActive?.bg ??
          theme.navigationBar.login.dropDownBackground)} !important;
      }
      &&:hover {
        background: ${theme.navigationBar.login.dropDownHoverBackground} !important;
      }
    `}
`

const DropdownWrapper = styled(FlexBox) <{ isTargetEmpty?: boolean, isNewCha?: boolean }>`

  ${({ isTargetEmpty }) => isTargetEmpty
    ? css`
    margin-left: 0px;
  ` : css`
    margin-left: 24px;
  `};

  @media (max-width: ${LayoutConst.screen.l_16}px) {
    ${({ isTargetEmpty }) => isTargetEmpty
    ? css`
    margin-left: 0px;
  ` : css`
    margin-left: 14px;
  `};
  }

  @media (max-width: 1440px) {
    ${({ isTargetEmpty }) => !isTargetEmpty && css`
      margin-left: 16px;
    `};
  }

  ${({ isNewCha }) => isNewCha && css`
    svg.moreIcon path { fill: #FFFFFF !important; }
  `};
`;

const DropdownBodyWrapper = styled.div`

  .cs-container {
    > div {
      justify-content: flex-start; 
    }
  }
  
  overflow-y: scroll;
  height: 100%;

  ::-webkit-scrollbar {
    width: 3px;
    background: ${(p) => p.theme.mobileSearch.scroll.background};
  }
  ::-webkit-scrollbar-thumb {
    background: ${(p) => p.theme.mobileSearch.scroll.body};
  }
  ::-webkit-scrollbar-track {
    background: ${(p) => p.theme.mobileSearch.scroll.background};
  }
`;

export const DropdownText = styled(ExternalLink) <{ isActive?: boolean, isMedia?: boolean, disabled?: boolean }>`
  width: 100%;
  font-size: ${({ isMedia }) => isMedia ? '20px' : '17px'};
  line-height: ${({ isMedia }) => isMedia ? '25px' : '22px'};
  letter-spacing: -0.02em;
  font-weight: 600 !important;
  color: ${p => {
    if (p?.isActive) return p.theme.mainDropdownStyles.colorActive;
    if (p?.disabled) return p.theme.mainDropdownStyles.disabled;
    return p.theme.mainDropdownStyles.color;
  }} !important;

  ${({ disabled }) => disabled && css`
    cursor: not-allowed;
  `};
  
  @media(max-width: ${LayoutConst.screen.l_16}px) {
    font-size: 15px;
    line-height: 20px;
  }

  @media (max-width: ${LayoutConst.screen.md}px) {
    font-size: 13px;
  }
`;

interface IExternalLinks {
  title: string | (() => string) | JSX.Element;
  value?: string | (() => string);
  ctx: string;
  active: boolean;
  external: boolean;
  isModal?: boolean;
  action?: (e?: any, unlockBody?: (withoutCheck?: boolean) => void) => void;
  isTeam?: boolean;
  name: string;
  disabled?: boolean;
  target?: string;
  className?: string;
}

interface IProps {
  target?: { [key: string]: string },
  list: {
    [key: string]: string,
  } | (string | { active: boolean, content: string, linkTo: string })[];
  initialTab?: string;
  hoverOpen?: boolean;
  withExternal?: boolean;
  isDark?: boolean;
}

export const DropdownWithExternalsLinks: FC<{
  target?: ReactElement;
  list: IExternalLinks[];
  initialTab?: string;
  hoverOpen?: boolean;
  maxHeight?: number;
  maxWidth?: number;
  customTarget?: boolean;
  isDarkTheme?: boolean;
  useCompetitionPanelColors?: boolean;
  noMargin?: boolean;
  isCustomTitle?: boolean;
  disableLink?: boolean;
}> = ({
  target,
  list,
  hoverOpen,
  maxHeight,
  isDarkTheme = false,
  maxWidth,
  customTarget,
  noMargin,
  isCustomTitle,
  disableLink = false,
  useCompetitionPanelColors = false,
}) => {
    const { t } = useTranslation();
    const theme = useTheme();
    const isDeziiignMain = AppTester.isDeziiignMain();
    const isHseDesign = AppTester.isHSEDesign();
    const isKiiids = AppTester.isKiiidsArt();
    const isMobile = useIsMobile();
    const isTablet = useBreakpoint(1199)
    const isAcademy = AppTester.isAcademy();
    const isFtbl = AppTester.isFtbl();
    const isPromtWeekend = AppTester.isPromtWeekend();
    const isNewCha = AppTester.isNewCha();

    const targetElem = target || '';
    const [lockBody, unlockBody] = useAdvancedLockBodyScroll();

    const isTargetEmpty = useMemo(() => (targetElem === '' || noMargin), [targetElem, noMargin])

    const dropdownTarget = useMemo(() => {
      return customTarget ? target :
        isDeziiignMain ? (
          <MoreButton />
        ) : (
          <ArrowTopIconWithText
            fill={'#909090'}
            text={''}
            noIconPadding
          />
        )
    }, [customTarget, isDeziiignMain, target])

    if (!list) {
      return null;
    }

    const customTheming = theme.navigationBar.login;
    const darkDropdownPanelBg =
      useCompetitionPanelColors && theme.competition?.langComponent?.notActive?.bg
        ? theme.competition.langComponent.notActive.bg
        : customTheming.dropdownContentBg;

    const currentNavPanelBg = theme.navigationBar.login.dropdownContentBg;
    const isGreyNavLoginPanel =
      currentNavPanelBg === REF_NAV_LOGIN_DROPDOWN_CONTENT_BG.grey ||
      currentNavPanelBg === REF_NAV_LOGIN_DROPDOWN_CONTENT_BG.additionalGrey;
    const isBlackNavLoginPanel = currentNavPanelBg === REF_NAV_LOGIN_DROPDOWN_CONTENT_BG.black;
    const brgDropdownUseDarkFlag =
      isDarkTheme && (isBlackNavLoginPanel || !isGreyNavLoginPanel);

    const items = useMemo(() => {
      return  Array.isArray(list) && list?.map((item, idx) => {
        const {
          title: initialTitle,
          isTeam,
          action,
          external,
          value,
          active,
          disabled,
          target: linkTargetInitial,
          className,
        } = item;

        const title = (() => {
          const resolvedTitle = typeof initialTitle === 'function' ? initialTitle() : initialTitle;
          if (typeof resolvedTitle === 'object') {
            return resolvedTitle;
          }
          if (isCustomTitle || isTeam) {
            return resolvedTitle?.toLowerCase();
          }

          return resolvedTitle;
        })();

        const linkTarget = linkTargetInitial || (external ? '_blank' : '_self');
        const tooltipTitle = typeof title === 'string' ? title : undefined;
        const isDafesCountries = className === 'dafes-countries'

        return (
          <DropdownItem
            $competitionRow={useCompetitionPanelColors}
            style={{ cursor: 'pointer' }}
            key={idx}
            justify='space-between'
            isActive={false}
            disabled={disabled}
            disabledHover
            theming={isAcademy ? customTheming : ''}
            className={className as never}
          >
            <DropdownText
              to={disableLink ? undefined : typeof value === 'function' ? value() : value}
              target={linkTarget}
              title={tooltipTitle}
              disabled={disabled}
              onClick={(ev: any) => {
                if (disabled) {
                  ev.preventDefault();
                }
                if (action) {
                  disabled ? null : action?.(ev, unlockBody);
                }
              }}
            >
              {title}
            </DropdownText>
            {!isDafesCountries && <ApprovedIcon fill={Colors.galBlack100} display={active}/>}
          </DropdownItem>
        )
      })
    }, [list, disableLink, useCompetitionPanelColors, isAcademy, isCustomTitle, theme])

    return (
      <DropdownWrapper
        justify="center"
        align="center"
        isTargetEmpty={isTargetEmpty}
        isNewCha={isNewCha}
        className={isDarkTheme ? 'brg-externals-dropdown--dark' : undefined}
        style={
          isDarkTheme
            ? ({
                ['--brg-externals-dropdown-scrollbar-bg' as string]: darkDropdownPanelBg,
              } as CSSProperties)
            : undefined
        }
      >
        {isDarkTheme ? <ExternalsDarkDropdownScrollbar /> : null}
        <Dropdown
          offsetY={12}
          offsetX={isHseDesign ? (isMobile ? -80 : -180) : -100}
          dark={brgDropdownUseDarkFlag}
          {...(isDarkTheme
            ? { dropdownPopupBg: darkDropdownPanelBg, dropdownContentBg: darkDropdownPanelBg }
            : {})}
          offsetArrowX={26}
          offsetArrowY={0}
          hideArrow
          hoverOpen={!isKiiids && !isTablet}
          maxHeight={maxHeight}
          maxWidth={maxWidth}
          target={dropdownTarget}
        >
          <DropdownBodyWrapper
            onMouseEnter={() => lockBody()}
            onClick={() => {
                if (isFtbl || isPromtWeekend) return;
                lockBody();
            }}
            onMouseLeave={() => unlockBody(false)}
          >
            {items}
          </DropdownBodyWrapper>
        </Dropdown>
      </DropdownWrapper>
    )
  }
